// Board view — to do / in motion / done columns with drag-between.
// Law 1: three states, only these words (UX-9 caught 'In Progress' surviving here).
// Extracted from DailyFocusTool.js (refactor phase 1).
// Oct 7 2026: rebuilt in the comp language (composite3). Columns are invisible
// containers — no pastel wells; cells carry a 3px status edge; lane labels are
// tinted monospace. Every color comes from the active theme's own tokens, so
// all themes keep working. Behavior (drag, reorder, click, star, complete)
// is unchanged.
import React from 'react';
import { Circle, CheckCircle2 } from 'lucide-react';

const MONO = "ui-monospace, 'SF Mono', SFMono-Regular, Menlo, monospace";
const fmtDay = (d) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const isPastDay = (d) => { const t = new Date(); t.setHours(0, 0, 0, 0); return new Date(d) < t; };
// One faint line under the title, only when there is something to say:
// the overdue date first, else the do day, else the steps left. Absolute
// counts only — no denominators (the law).
const cardHint = (action) => {
  if (action.completed || action.status === 'done') return null;
  if (action.dueDate && isPastDay(action.dueDate)) return { text: `Overdue ${fmtDay(action.dueDate)}`, tone: 'var(--color-error)' };
  if (action.doDate) return { text: `Do ${fmtDay(action.doDate)}`, tone: 'var(--color-textMuted)' };
  const steps = (action.checklist || []).filter(c => !c.completed).length;
  if (steps > 0) return { text: `${steps} step${steps === 1 ? '' : 's'} left on the checklist`, tone: 'var(--color-textMuted)' };
  return null;
};
const LANE = {
  todo:  'var(--color-primary)',
  doing: 'var(--color-warning)',
  done:  'var(--color-success)',
};

export default function KanbanBoard({
  actionsToShow,
  completedActionsToShow,
  groupByProject,
  soloProject, // one project fills the board — naming it on every card is noise
  dragOverCol,
  setDragOverCol,
  setActionStatus,
  selectedActionId,
  setSelectedActionId,
  projects,
  getProjectColorById,
  toggleActionPho,
  onReorderCard,
}) {
  return (
                  <div className="flex gap-3 p-4 min-h-[calc(100vh-200px)] items-start">
                    {[
                      { id: 'todo',  label: 'open' },
                      { id: 'doing', label: 'active' },
                      { id: 'done',  label: 'done' },
                    ].map(col => {
                      const allForKanban = col.id === 'done'
                        ? [...actionsToShow, ...completedActionsToShow]
                        : actionsToShow;
                      const colActions = allForKanban.filter(g => {
                        if (col.id === 'done')  return g.completed || g.status === 'done';
                        if (col.id === 'doing') return !g.completed && g.status === 'doing';
                        return !g.completed && g.status !== 'doing' && g.status !== 'done';
                      });
                      const isOver = dragOverCol === col.id;
                      const laneColor = LANE[col.id];
                      return (
                        <div
                          key={col.id}
                          className="flex-1 flex flex-col min-w-0"
                          onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
                          onDragLeave={() => setDragOverCol(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setDragOverCol(null);
                            const actionId = parseInt(e.dataTransfer.getData('actionId'));
                            setActionStatus(actionId, col.id);
                          }}
                        >
                          <div className="flex items-baseline gap-1.5 mb-2 px-1">
                            <span className="text-[10.5px] font-semibold select-none"
                              style={{ fontFamily: MONO, color: laneColor }}>{col.label}</span>
                            <span className="text-[10.5px] tabular-nums select-none"
                              style={{ fontFamily: MONO, color: 'var(--color-faint)' }}>{colActions.length}</span>
                          </div>
                          <div className="flex-1 rounded-lg p-1 space-y-2 min-h-[120px] transition-colors duration-150"
                            style={isOver
                              ? { outline: `2px dashed color-mix(in srgb, ${laneColor} 55%, transparent)`, outlineOffset: 2, background: `color-mix(in srgb, ${laneColor} 6%, transparent)` }
                              : undefined}
                          >
                            {(groupByProject
                              ? (() => {
                                  const sorted = [...colActions].sort((a, b) => {
                                    const pa = projects.find(p => p.id === a.linkId)?.name || '';
                                    const pb = projects.find(p => p.id === b.linkId)?.name || '';
                                    return pa.localeCompare(pb);
                                  });
                                  const items = [];
                                  let lastProjId = undefined;
                                  sorted.forEach(action => {
                                    const projId = action.linkType === 'project' ? action.linkId : null;
                                    if (projId !== lastProjId) {
                                      lastProjId = projId;
                                      const proj = projects.find(p => p.id === projId);
                                      items.push({ type: 'divider', proj, key: `d-${col.id}-${projId}` });
                                    }
                                    items.push({ type: 'action', action });
                                  });
                                  return items;
                                })()
                              : colActions.map(action => ({ type: 'action', action }))
                            ).map(item => {
                              if (item.type === 'divider') {
                                return (
                                  <div key={item.key} className="flex items-center gap-1.5 pt-1 pb-0.5 px-1">
                                    {item.proj && <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: getProjectColorById(item.proj.id) }} />}
                                    <span className="text-[10px] font-semibold truncate select-none"
                                      style={{ fontFamily: MONO, color: 'var(--color-faint)' }}>
                                      {item.proj ? item.proj.name : 'No project'}
                                    </span>
                                  </div>
                                );
                              }
                              const { action } = item;
                              const proj = projects.find(p => p.id === action.linkId);
                              const struck = action.completed || action.status === 'done';
                              const selectedCard = action.id === selectedActionId;
                              return (
                                <div
                                  key={action.id}
                                  draggable
                                  onDragStart={(e) => e.dataTransfer.setData('actionId', action.id.toString())}
                                  onDragOver={(e) => { if (onReorderCard) { e.preventDefault(); e.stopPropagation(); } }}
                                  onDrop={(e) => {
                                    if (!onReorderCard) return;
                                    e.preventDefault();
                                    e.stopPropagation(); // beat the column-level drop
                                    const draggedId = parseInt(e.dataTransfer.getData('actionId'));
                                    const r = e.currentTarget.getBoundingClientRect();
                                    if (draggedId) onReorderCard(draggedId, action.id, e.clientY < r.top + r.height / 2, col.id);
                                  }}
                                  onClick={() => setSelectedActionId(action.id === selectedActionId ? null : action.id)}
                                  className="rounded-md p-3 cursor-pointer select-none transition-colors duration-150 hover:themed-surface-hover"
                                  style={{
                                    background: action.completed ? 'var(--color-landed)' : 'var(--color-surface)',
                                    border: `1px solid ${selectedCard ? 'var(--color-primary)' : 'var(--color-border)'}`,
                                    borderLeft: `3px solid ${laneColor}`,
                                  }}
                                >
                                  <div className="flex items-start gap-2">
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setActionStatus(action.id, col.id === 'done' ? 'todo' : 'done'); }}
                                      className="flex-shrink-0 mt-0.5 transition-transform hover:scale-110"
                                      style={{ color: struck ? 'var(--color-success)' : 'var(--color-textMuted)', opacity: struck ? 1 : 0.55 }}
                                    >
                                      {struck ? <CheckCircle2 size={14}/> : <Circle size={14}/>}
                                    </button>
                                    <span className="text-[12.5px] leading-snug flex-1"
                                      style={struck
                                        ? { color: 'var(--color-textMuted)', textDecoration: 'line-through', textDecorationColor: 'color-mix(in srgb, var(--color-success) 50%, transparent)', textDecorationThickness: 1 }
                                        : { color: 'var(--color-text)' }}>
                                      {action.text}
                                      {action.completed && action.resolutionComment && (
                                        <span className="block text-[12px] mt-1"
                                          style={{ color: 'var(--color-success)', textDecoration: 'none' }}>
                                          ↳ {action.resolutionComment}
                                        </span>
                                      )}
                                    </span>
                                    <button
                                      onClick={(e) => { e.stopPropagation(); toggleActionPho(action.id); }}
                                      className={`flex-shrink-0 p-1 -my-1 text-base leading-none rounded transition-opacity duration-100 ${action.pho ? 'opacity-100' : 'opacity-25 hover:opacity-100'}`}
                                      style={{ color: action.pho ? 'var(--color-pho)' : 'var(--color-textMuted)' }}
                                      title={action.pho ? 'Unmark focus' : 'Mark as focus (floats to today)'}
                                    >✦</button>
                                  </div>
                                  {(() => {
                                    const hint = cardHint(action);
                                    return hint && (
                                      <div className="mt-1.5 ml-5 text-[11px] truncate" style={{ color: hint.tone }}>{hint.text}</div>
                                    );
                                  })()}
                                  {proj && !soloProject && (
                                    <div className="flex items-center gap-1.5 mt-2 ml-5">
                                      <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{backgroundColor: proj.color || 'var(--color-border)'}}/>
                                      <span className="text-[10px] truncate" style={{ color: 'var(--color-textMuted)' }}>{proj.name}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
  );
}
